/**
 * TippyManager
 * ------------
 * Centralizes Tippy creation and tooltip configuration.
 *
 * Preferred attributes:
 *   data-tooltip="page|url"
 *   data-tooltip-trigger="hover|click"
 *   data-tooltip-content="..."
 *   data-tooltip-source="..."
 *
 * Legacy data-tippy-* attributes are supported during migration.
 *
 * Rule: one DOM element = one Tippy instance.
 */
class TippyManager {
    constructor(tippy, options = {}) {
        if (!tippy) {
            throw new Error('TippyManager: tippy is required');
        }
        if (!options.translator) {
            throw new Error('TippyManager: translator is required');
        }

        this.tippy = tippy;
        this.translator = options.translator;
        this.options = Object.assign({
            theme: 'light',
            allowHTML: true,
            cacheRemote: true,
            errorContent: this.translator.trans('tooltip.loadError'),
        }, options);

        this.remoteCache = new Map();
        this.states = new WeakMap();
    }

    get selector() {
        return '[data-tooltip], [data-tippy], [data-action="confirm"]';
    }

    /**
     * Initialize tooltips in root, including root itself when applicable.
     *
     * @param {ParentNode|Element} root
     */
    init(root = document) {
        this._collect(root, this.selector).forEach((element) => {
            this.getOrCreate(element);
        });
    }

    /**
     * Read preferred attributes first, then legacy data-tippy-* attributes.
     */
    getConfig(element) {
        const dataset = element.dataset || {};

        return {
            type: dataset.tooltip || dataset.tippy || 'page',
            event: dataset.tooltipTrigger || dataset.tippyEvent || 'hover',
            content: dataset.tooltipContent ?? dataset.tippyContent ?? '',
            source: dataset.tooltipSource || dataset.tippySource || null,
            action: dataset.action || null,
        };
    }

    /**
     * Return the one Tippy instance associated with an element.
     */
    getOrCreate(element) {
        if (element._tippy) {
            this._rememberState(element, element._tippy);
            return element._tippy;
        }

        const config = this.getConfig(element);
        const isConfirm = config.action === 'confirm';
        const content = this._resolvePageContent(config);

        const hasHoverTooltip = isConfirm && config.event === 'hover' && this._hasTooltip(element);

        const props = {
            content,
            allowHTML: this.options.allowHTML,
            theme: this.options.theme,
            trigger: isConfirm
                ? (hasHoverTooltip ? 'mouseenter' : 'manual')
                : this._toTippyTrigger(config.event),
            interactive: !isConfirm && config.event === 'click',
            hideOnClick: true,
        };

        if (config.type === 'url' && config.source) {
            props.onShow = async (instance) => {
                instance.setContent(await this.fetchContent(config.source));
            };
        }

        const instance = this.tippy(element, props);
        this._rememberState(element, instance, props, content);

        return instance;
    }


    /**
     * Resolve current tooltip content without changing visibility.
     */
    async resolveContent(element) {
        const config = this.getConfig(element);

        if (config.type === 'url' && config.source) {
            return this.fetchContent(config.source);
        }

        return this._resolvePageContent(config);
    }

    getBaseContent(element) {
        const instance = element._tippy;
        const state = instance ? this.states.get(element) : null;
        return state?.baseContent ?? this._resolvePageContent(this.getConfig(element));
    }

    /**
     * Show the normal tooltip behaviour on an element.
     * Used by TippyConfirm, which owns hover/click events on confirm actions.
     */
    async showTooltip(element, guard = null) {
        const instance = this.getOrCreate(element);
        const state = this._rememberState(element, instance);
        const config = this.getConfig(element);

        let content = state.baseContent;

        if (config.type === 'url' && config.source) {
            content = await this.fetchContent(config.source);
        } else if (config.type === 'page') {
            content = this._resolvePageContent(config);
            state.baseContent = content;
        }

        if (guard && !guard()) {
            return instance;
        }

        this.restore(element);
        instance.setContent(content);
        instance.show();

        return instance;
    }

    /**
     * Restore the normal tooltip props after a component temporarily changes
     * the shared Tippy instance (e.g. TippyConfirm).
     */
    restore(element) {
        const instance = element._tippy;
        const state = instance ? this.states.get(element) : null;

        if (!instance || !state) {
            return instance || null;
        }

        instance.setProps({...state.baseProps});
        instance.setContent(state.baseContent);

        return instance;
    }

    hide(element) {
        element._tippy?.hide();
    }

    destroy(element) {
        if (element._tippy) {
            element._tippy.destroy();
        }
        this.states.delete(element);
    }

    clearRemoteCache(url = null) {
        if (url) {
            this.remoteCache.delete(url);
            return;
        }

        this.remoteCache.clear();
    }

    async fetchContent(url) {
        if (this.options.cacheRemote && this.remoteCache.has(url)) {
            return this.remoteCache.get(url);
        }

        try {
            const response = await fetch(url, {
                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                },
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            const content = await response.text();

            if (this.options.cacheRemote) {
                this.remoteCache.set(url, content);
            }

            return content;
        } catch (error) {
            console.error('TippyManager: unable to load tooltip content', error);
            return this.options.errorContent;
        }
    }

    _rememberState(element, instance, props = null, content = null) {
        let state = this.states.get(element);

        if (!state) {
            const currentProps = instance.props || props || {};
            const baseProps = {
                allowHTML: currentProps.allowHTML,
                theme: currentProps.theme,
                trigger: currentProps.trigger,
                interactive: currentProps.interactive,
                placement: currentProps.placement,
                animation: currentProps.animation,
                appendTo: currentProps.appendTo,
                zIndex: currentProps.zIndex,
                hideOnClick: currentProps.hideOnClick,
                onShow: currentProps.onShow,
                onHide: currentProps.onHide,
                onShown: currentProps.onShown,
                onHidden: currentProps.onHidden,
                onTrigger: currentProps.onTrigger,
            };

            state = {
                instance,
                baseProps,
                baseContent: content ?? currentProps.content ?? '',
            };

            this.states.set(element, state);
        }

        return state;
    }

    _resolvePageContent(config) {
        let content = config.content || '';

        if (config.type !== 'page' || !config.source || typeof document === 'undefined') {
            return content;
        }

        const sourceElement = document.querySelector(config.source);

        if (sourceElement) {
            sourceElement.classList.add('d-none');
            content += sourceElement.innerHTML;
        }

        return content;
    }

    _hasTooltip(element) {
        const dataset = element.dataset || {};
        return Boolean(dataset.tooltip || dataset.tippy);
    }

    _toTippyTrigger(event) {
        return event === 'hover' ? 'mouseenter' : event;
    }

    _collect(root, selector) {
        const elements = [];

        if (!root) {
            return elements;
        }

        if (typeof root.matches === 'function' && root.matches(selector)) {
            elements.push(root);
        }

        if (typeof root.querySelectorAll === 'function') {
            elements.push(...root.querySelectorAll(selector));
        }

        return elements;
    }
}

export default TippyManager;
